// Chiptune / lofi 8-bit (docs/AUDIO_DESIGN.md §12): cozy game-menu music on the NES's four channels, through a warm tape.
// Two band-limited pulse waves (12.5 / 25 / 50 % duty) play the chords as chip arpeggios (16th picks up and down the
// voicing, a held chord's own 32nd roll, short stabs) with a quieter echo channel; a 4-bit stepped triangle bounces the
// bass in octaves; a triangle kick and the noise channel keep a soft beat; a pulse lead sings with delayed vibrato, slides
// and an echo. Game-music harmony: I–V–vi–IV, vi–IV–I–V, ♭VI–♭VII–I, i–♭VI–♭III–♭VII, triads, add9 and sus, few 7ths.
import { rng } from '../../engine/core.js';
import { QUALITIES } from '../harmony.js';
import { createChipChords, createTriBass, createChipKit, createChipLead } from './chiptune-voices.js';

const mod12 = n => ((n % 12) + 12) % 12;
const ENERGY = { chill: 0, balanced: 1, upbeat: 2 };
const pickW = (r, w) => {
  let u = r() * Object.values(w).reduce((a, b) => a + b, 0);
  for (const [k, v] of Object.entries(w)) if ((u -= v) < 0) return k;
  return Object.keys(w)[0];
};

/* ---------------- voicing: 4 notes over the root, close or open, in the chip register ---------------- */
// Shapes (semitones over the root) per chord type: every inversion of the triad with its bottom note doubled on top, and one
// open shape (R 5 R 3). add9 / madd9 / m7 are 4 tones: their inversions plus an open R 5 9 3 (R 5 ♭7 ♭3). The engine's final
// tonic (maj9 / 6/9 / m9) is played as add9 / madd9; any other quality is reduced to its triad. The lowest note sits at MIDI
// 50–62 and the top ≤ 77 (the lead sings 67–84 above), so the arpeggios stay warm under it.
const SHAPES = {
  chip:     [[0, 4, 7, 12], [4, 7, 12, 16], [7, 12, 16, 19], [0, 7, 12, 16]],
  chipm:    [[0, 3, 7, 12], [3, 7, 12, 15], [7, 12, 15, 19], [0, 7, 12, 15]],
  chipsus2: [[0, 2, 7, 12], [2, 7, 12, 14], [7, 12, 14, 19], [0, 7, 12, 14]],
  chipsus4: [[0, 5, 7, 12], [5, 7, 12, 17], [7, 12, 17, 19], [0, 7, 12, 17]],
  add9:     [[0, 4, 7, 14], [4, 7, 12, 14], [7, 12, 14, 16], [0, 7, 14, 16], [2, 4, 7, 12]],
  madd9:    [[0, 3, 7, 14], [3, 7, 12, 14], [7, 12, 14, 15], [0, 7, 14, 15], [2, 3, 7, 12]],
  m7:       [[0, 3, 7, 10], [3, 7, 10, 12], [7, 10, 12, 15], [0, 7, 10, 15]],
};
const FINAL = { maj9: 'add9', '69': 'add9', m9: 'madd9' };
const typeOf = c => {
  if (SHAPES[c.q]) return c.q;
  if (FINAL[c.q]) return FINAL[c.q];
  const iv = QUALITIES[c.q] || [0, 4, 7];
  return iv.includes(4) ? 'chip' : iv.includes(3) ? 'chipm' : iv.includes(5) ? 'chipsus4' : 'chipsus2';
};
const LOW = [50, 62], TOP = 77;
const CANDS = new Map();                                             // `${root}:${type}` → [midi[]]
function candidates(root, type) {
  const key = `${root}:${type}`;
  if (CANDS.has(key)) return CANDS.get(key);
  const out = [];
  for (const sh of SHAPES[type]) for (let o = 3; o <= 6; o++) {
    const v = sh.map(i => root + i + 12 * o);
    if (v[0] >= LOW[0] && v[0] <= LOW[1] && v[3] <= TOP) out.push(v);
  }
  CANDS.set(key, out);
  return out;
}
const mean = a => a.reduce((s, v) => s + v, 0) / a.length;
/** Cost Σ|Δ| + 1.5·max(0, |Δtop| − 3) + .3·|mean − 63|; the first chord .6·|mean − (63 + plan.register)| (issue #31: tracks
 *  don't all open on the same notes). The same chord keeps its voicing (every 16th of an arpeggio cell asks again). */
export function chipVoicing(sp, prev, c, plan) {
  const root = mod12(plan.key.tonic + c.root), type = typeOf(c), cands = candidates(root, type);
  if (prev && prev.length === 4 && cands.some(v => v.every((m, k) => m === prev[k]))) return prev;
  let best = cands[0], bc = Infinity;
  for (const v of cands) {
    let k;
    if (prev && prev.length === 4) {
      k = 0.3 * Math.abs(mean(v) - 63) + 1.5 * Math.max(0, Math.abs(v[3] - prev[3]) - 3);
      for (let i = 0; i < 4; i++) k += Math.abs(v[i] - prev[i]);
    } else k = 0.6 * Math.abs(mean(v) - 63 - (plan.register || 0));
    if (k < bc) { bc = k; best = v; }
  }
  return best;
}

/* ---------------- comping: arpeggio cells of [step, length, velocity, pick] ---------------- */
// pick = voicing indices (0 = the lowest). A cell without picks strikes the whole chord: held (a roll, the instrument's own
// 32nd arpeggio) or a stab (≤ 2 steps). The 16th cells accent the beats (1 · .78 · .64 on the 8ths and 16ths between).
const ACC = s => (s % 4 === 0 ? 1 : s % 2 === 0 ? 0.78 : 0.64);
const sixteenths = seq => Array.from({ length: 16 }, (_, s) => [s, 1, ACC(s), [seq[s % seq.length]]]);
const COMPS = {
  hold:    [[0, 16, 0.9]],                                                                   // a roll the whole chord
  half:    [[0, 8, 0.9], [8, 8, 0.8]],                                                       // rolls on 1 and 3
  arp:     sixteenths([0, 1, 2, 3]),                                                         // up, a note a 16th
  arpUD:   sixteenths([0, 1, 2, 3, 2, 1]),                                                   // up and down (a 6-note cycle)
  alberti: sixteenths([0, 3, 1, 3, 2, 3, 1, 3]),                                             // the top as a pedal between
  run8:    [0, 1, 2, 3, 3, 2, 1, 2].map((k, i) => [2 * i, 2, i % 2 ? 0.72 : 0.95, [k]]),     // 8ths up and back
  mix:     [[0, 8, 0.9], ...sixteenths([0, 1, 2, 3]).filter(([s]) => s >= 8)],               // a roll, then 16ths
  stab:    [[0, 2, 1], [3, 1, 0.75], [6, 2, 0.85], [10, 1, 0.8], [12, 2, 0.85]],             // short chords
  off:     [[2, 1, 0.85], [6, 1, 0.9], [10, 1, 0.85], [14, 1, 0.9]],                         // off-beat stabs
};
const ARPS = ['arp', 'arpUD', 'alberti', 'run8', 'mix'];

/* ---------------- bass: the triangle's octave-jumping cells ---------------- */
// One letter a 16th: R root · o the octave up · 5 the fifth · . rest; 'kick' follows the kick grid. A root on every chord
// change, an approach into the next root on step 14 or 15. The triangle sits an octave above the default bass (MIDI 38–50,
// octave notes to 62), where the NES plays it and a phone hears it.
const BASS_CELLS = {
  hold:   'R...............',
  half:   'R.......R.......',
  lazy:   'R.......R.....o.',
  walk:   'R...5...o...5...',
  oct:    'R.o.R.o.R.o.R.o.',
  bounce: 'R..R..o.R..R..o.',
  skip:   'R.o...o.R.o...o.',
  gallop: 'R.oRR.o.R.oRR.o.',
};
const BASS_POOL = [
  { hold: 0.8, half: 1.2, lazy: 1.2, walk: 0.5, kick: 0.5 },
  { lazy: 0.6, walk: 0.8, oct: 1.2, bounce: 1, skip: 0.8, kick: 0.5 },
  { oct: 1.4, bounce: 1, skip: 1, gallop: 0.8, walk: 0.3 },
];
const triNote = (pc, prev = 44) => {
  let best = 38 + mod12(pc - 38), bd = Infinity;
  for (let n = best; n <= 50; n += 12) { const d = Math.abs(n - prev); if (d < bd) { bd = d; best = n; } }
  return best;
};

/* ---------------- melody: a game-tune motif (8th runs, dotted skips) in place of the jazzhop one ---------------- */
// Rhythm cells (16th steps over 2 bars) of 8ths and dotted pairs; the contour moves a scale step (p .5), two (p .3, a
// chord-tone skip in the pentatonic) or leaps three and steps back (p .2), turning around past ±4; the last note lasts
// 4–8 steps. The engine still picks the notes (the key's pentatonic, chord tones on beats 1 and 3).
const MCELLS = [[0, 2, 4, 6, 8], [0, 4, 6, 8, 12], [0, 3, 6, 8, 10], [0, 2, 4, 8, 12], [2, 4, 6, 8, 14], [0, 6, 8, 10, 12], [0, 4, 8, 10, 12, 14], [0, 2, 6, 8, 12]];
const MTAILS = [[], [16], [16, 20], [16, 18, 20], [20], [16, 24]];
function chipMotif(r, key) {
  const steps = MCELLS[Math.floor(r() * MCELLS.length)].concat(MTAILS[Math.floor(r() * MTAILS.length)]).slice(0, 7), n = steps.length;
  const last = 4 + Math.floor(r() * 5), durs = steps.map((s, i) => (i < n - 1 ? Math.min(4, steps[i + 1] - s) : last));
  const contour = [0];
  let dir = r() < 0.6 ? 1 : -1, back = 0;
  for (let i = 1; i < n; i++) {
    const u = r();
    let mv;
    if (back) { mv = -back; back = 0; } else if (u < 0.5) mv = dir; else if (u < 0.8) mv = 2 * dir; else { mv = 3 * dir; back = dir; }
    contour.push(contour[i - 1] + mv);
    if (Math.abs(contour[i]) >= 4) dir = -Math.sign(contour[i]);
  }
  const vel = steps.map((s, i) => (i === 0 ? 0.8 : s % 4 === 0 ? 0.72 : 0.6 + 0.08 * r()));
  return { steps, durs, contour, vel, mode: key.mode };
}

/* ---------------- drums: the noise channel and a triangle kick ---------------- */
// K triangle kick · S noise snare · H noise hat (o open) · X noise shaker · R pulse blip · T triangle tom (fills)
const GRIDS = {
  //           0123456789abcdef
  save:      { K: 'x.....x...x.....', S: '........x.......', H: 'x.x.x.x.x.x.x.x.', R: '............s...' },   // halftime
  town:      { K: 'x.........x.....', S: '....x.......x...', H: 'x.x.x.x.x.x.x.xs' },                          // boom-bap
  hop:       { K: 'x......x..x.....', S: '....x.......x...', H: 'x.xsx.xsx.xsx.xs' },
  overworld: { K: 'x.....x.x.......', S: '....x.......x...', H: 'xsxsxsxsxsxsxsxs' },                          // 16th hats
  bounce:    { K: 'x..x....x.x.....', S: '....x.......x..g', H: 'x.x.x.x.x.x.x.x.', X: '..s...s...s...s.' },
  break:     { K: 'x.........x.....', R: '....x.......x...', H: 'x...x...x...x...' },
};
const GROOVES = {
  chill: { save: 1.4, town: 1.2, hop: 0.5 },
  balanced: { town: 1, hop: 1, save: 0.5, overworld: 0.6, bounce: 0.4 },
  upbeat: { overworld: 1.2, bounce: 1, hop: 0.8, town: 0.5 },
};

export default {
  id: 'chiptune', name: 'Chiptune', desc: '8-bit pulse arpeggios, a triangle bass and a soft noise-channel beat',
  //            chill                                   balanced                                  upbeat
  vary: 'colour',
  energy: {
    stretch:  [0.4,                                     0.25,                                     0.15],
    bpm:      [[76, 84],                                [82, 94],                                 [90, 104]],
    swing:    [[52, 57],                                [51, 55],                                 [50, 53]],
    snareLag: [[0.004, 0.012],                          [0.002, 0.008],                           [0, 0.005]],
    groove:   [[GROOVES.chill, GROOVES.chill],          [GROOVES.balanced, GROOVES.balanced],     [GROOVES.upbeat, GROOVES.upbeat]],
    drumLevel:[0.8,                                     0.9,                                      0.92],
    kitLp:    [[5000, 7000],                            [6000, 8000],                             [6800, 9000]],
    epLp:     [[1900, 2600],                            [2200, 3100],                             [2500, 3500]],   // the pulse bus LP
    strum:    [[0.003, 0.008],                          [0.002, 0.006],                           [0.001, 0.004]],
    epVel:    [[0.5, 0.6],                              [0.54, 0.64],                             [0.56, 0.68]],
    comp: [
      { hold: [0.6, 1.2], half: [0.3, 0.8], run8: [0.4, 1], arp: [0.1, 0.4], arpUD: [0.1, 0.4], alberti: [0.1, 0.4], mix: [0.1, 0.4], stab: [0, 0.1], off: [0, 0.05] },
      { hold: [0.2, 0.6], half: [0.2, 0.6], run8: [0.3, 0.8], arp: [0.4, 0.9], arpUD: [0.3, 0.8], alberti: [0.3, 0.8], mix: [0.3, 0.7], stab: [0.1, 0.4], off: [0, 0.2] },
      { hold: [0, 0.2], half: [0.1, 0.4], run8: [0.2, 0.6], arp: [0.5, 1], arpUD: [0.4, 0.9], alberti: [0.3, 0.8], mix: [0.3, 0.7], stab: [0.3, 0.8], off: [0.2, 0.6] },
    ],
    push:     [0.1,                                     0.18,                                     0.28],
    legato:   [0.7,                                     0.5,                                      0.3],
    lead:     [0.62,                                    0.75,                                     0.85],
    softLead: [0.6,                                     0.5,                                      0.4],            // 'soft' = a rounder duty
    tone:     [[4800, 7000],                            [5800, 8500],                             [6800, 9500]],
    wowCents: [[5, 10],                                 [4, 8],                                   [3, 7]],
    dust:     [[0.8, 1.4],                              [0.6, 1.2],                               [0.5, 1]],
    arpRate:  [{ 0.5: 1, 1: 0.6 },                      { 0.5: 1, 0.333: 0.3, 1: 0.2 },           { 0.5: 1, 0.333: 0.5 }],   // a roll's note, in 16ths
    duty:     [{ 0.125: 0.2, 0.25: 1, 0.5: 1.4 },       { 0.125: 0.5, 0.25: 1.4, 0.5: 1 },        { 0.125: 0.7, 0.25: 1.4, 0.5: 0.8 }],   // the chords' pulse
    // development (arrange.js BY_ENERGY): a cold start like a game's title screen
    intro:      [{ keys: 0.2, beat: 0.2, groove: 0.2, lead: 0.15, cold: 0.25 }, { keys: 0.2, beat: 0.2, groove: 0.2, lead: 0.15, cold: 0.25 }, { keys: 0.2, beat: 0.2, groove: 0.2, lead: 0.15, cold: 0.25 }],
  },
  qualities: { chip: [0, 4, 7], chipm: [0, 3, 7], chipsus2: [0, 2, 7], chipsus4: [0, 5, 7] },
  progressions: {
    major: {
      G1: [[0, 'add9', 4], [7, 'chip', 4], [9, 'chipm', 4], [5, 'add9', 4]],                            // I V vi IV
      G2: [[9, 'chipm', 4], [5, 'add9', 4], [0, 'chip', 4], [7, 'chipsus4', 2], [7, 'chip', 2]],       // vi IV I Vsus4|V
      G3: [[5, 'add9', 4], [7, 'chip', 4], [4, 'chipm', 4], [9, 'm7', 4]],                              // IV V iii vi7 (the JRPG royal road)
      G4: [[0, 'add9', 4], [8, 'chip', 4], [10, 'chip', 4], [0, 'chipsus2', 2], [0, 'chip', 2]],       // I ♭VI ♭VII Isus2|I
      G5: [[0, 'chip', 4], [4, 'chipm', 4], [5, 'add9', 4], [5, 'chipm', 4]],                           // I iii IV iv
      G6: [[0, 'add9', 4], [9, 'chipm', 4], [2, 'm7', 4], [7, 'chipsus4', 4]],                          // I vi ii7 Vsus4 (the menu loop)
    },
    minor: {
      g1: [[0, 'madd9', 4], [8, 'chip', 4], [3, 'chip', 4], [10, 'chip', 4]],                           // i ♭VI ♭III ♭VII
      g2: [[0, 'chipm', 4], [10, 'chip', 4], [8, 'chip', 4], [10, 'chipsus4', 2], [10, 'chip', 2]],    // i ♭VII ♭VI ♭VIIsus4|♭VII
      g3: [[8, 'add9', 4], [10, 'chip', 4], [0, 'madd9', 4], [0, 'chipm', 4]],                          // ♭VI ♭VII i i
      g4: [[0, 'madd9', 4], [5, 'chipm', 4], [10, 'chip', 4], [3, 'add9', 4]],                          // i iv ♭VII ♭III
      g5: [[0, 'chipm', 4], [8, 'chip', 4], [5, 'chipm', 4], [7, 'chipsus4', 2], [7, 'chip', 2]],      // i ♭VI iv Vsus4|V
      g6: [[0, 'madd9', 4], [7, 'chipm', 4], [8, 'chip', 4], [10, 'chip', 4]],                          // i v ♭VI ♭VII
    },
  },
  // the bar that ends progB when it repeats progA: IV|V (♭VI|♭VII in minor), the game-music cadence
  turnarounds: { major: [[5, 'chip', 2], [7, 'chip', 2]], minor: [[8, 'chip', 2], [10, 'chip', 2]] },
  grids: GRIDS,
  fills: {
    T1: { roll: [[12, 0.7], [13, 0.6], [14, 0.7], [15, 0.8]], voice: 'tom' },                        // triangle toms, falling
    S1: { roll: [[12, 0.6], [13, 0.45], [14, 0.6], [15, 0.75]] },                                   // a noise roll
    S2: { roll: [[8, 0.35], [10, 0.45], [12, 0.55], [13, 0.5], [14, 0.65], [15, 0.8]] },            // a longer build
    K1: { kicks: [13, 15] },
    X1: { stop: 12 },
  },
  comps: COMPS,
  keysLevel: 1.42,                                                   // Chords only: +3 dB, as jazzhop

  plan(plan, { r, E, energy }) {
    const e = ENERGY[energy] ?? 1, pick = (a, b, d = 3) => +(a + r() * (b - a)).toFixed(d);
    // game music loops one arpeggio pattern: one arp cell a track weighs ×3
    const loop = pickW(r, Object.fromEntries(ARPS.map(id => [id, plan.ep.comp[id] + 0.05])));
    plan.ep.comp[loop] = +(plan.ep.comp[loop] * 3 + 0.4).toFixed(2);
    const soft = !!plan.lead && plan.lead.timbre === 'soft', u = r();
    plan.chip = {
      chords: {
        duty: +pickW(r, E.duty), arp: +pickW(r, E.arpRate), shape: pickW(r, { up: 1.2, updown: 1, down: 0.4 }),
        decay: pick(0.1, 0.26), sus: pick(0.5, 0.7, 2), echo: { steps: r() < 0.6 ? 3 : 2, wet: pick(0.24, 0.38, 2), pan: pick(0.35, 0.55, 2) },
      },
      bass: { cells: [pickW(r, BASS_POOL[e]), pickW(r, BASS_POOL[e])], approach: [0.3, 0.4, 0.45][e], lp: pick(1600, 2600, 0) },
      lead: {
        duty: soft ? (u < 0.6 ? 0.5 : 0.25) : (u < 0.6 ? 0.25 : 0.125), lp: pick(1600, 2300, 0), vib: pick(12, 24, 1), vibRate: pick(5.2, 6.4, 2),
        vibDelay: pick(0.16, 0.3), bend: pick(0.12, 0.3, 2), slide: pick(0.3, 0.55, 2), glide: pick(0.02, 0.04),
        echo: r() < 0.5 ? 3 : 2, fb: pick(0.18, 0.32, 2), wet: pick(0.3, 0.45, 2),
      },
    };
    Object.assign(plan.drums.kit, {
      kickF0: pick(160, 200, 1), kickF1: pick(46, 54, 1), kickSweep: pick(0.02, 0.032), kickDecay: pick(0.07, 0.11),
      snareBP: pick(1300, 2000, 0), snareHold: r() < 0.5 ? 2 : 3, snareDecay: pick(0.06, 0.1), snareBody: pick(210, 250, 0),
      hatHP: pick(6000, 8000, 0), hatClosed: pick(0.009, 0.015), hatOpen: pick(0.08, 0.12), rimF: pick(1100, 1500, 0),
    });
    if (plan.lead) plan.lead.motif = chipMotif(r, plan.key);
  },

  voicing: chipVoicing,
  /** One bar of triangle bass: the track's cells in the main groove; held roots in the intro's last bar, the break and the
   *  outro (the final one rings a bar and a half). */
  bass({ plan, st, b, mode, chords, nextChords, chordAt, kicks, stop }) {
    const C = plan.chip.bass, r = st.rS, pc = c => mod12(plan.key.tonic + c.root), end = stop ?? 16;
    if (mode !== 'kick') {
      const out = [];
      for (const c of chords) {
        const s = c.start * 4;
        if (s >= end) continue;
        const m = triNote(pc(c), st.prevBass);
        st.prevBass = m;
        out.push({ s, midi: m, vel: 0.8, dur: c.final ? 24 : Math.min(end - s, c.beats * 4) * 0.92 });
      }
      return out;
    }
    const id = b.j % 4 === 3 ? C.cells[r() < 0.6 ? 1 : 0] : C.cells[r() < 0.85 ? 0 : 1];
    let hits = id === 'kick' ? kicks.map(s => [s, 'R']) : [...BASS_CELLS[id]].map((c, s) => [s, c]).filter(([, c]) => c !== '.');
    for (const c of chords) { const s0 = c.start * 4; hits = hits.filter(([s]) => s !== s0); hits.push([s0, 'R']); }   // a root on every change
    hits.sort((x, y) => x[0] - y[0]);
    const last = chords[chords.length - 1], nx = nextChords && nextChords[0];
    if (nx && nx.root !== last.root && r() < C.approach) {                   // approach the next root on step 14 or 15
      const s = r() < 0.5 ? 14 : 15;
      hits = hits.filter(([h]) => h < s); hits.push([s, 'A']);
    }
    hits = hits.filter(([s]) => s < end);
    return hits.map(([s, c], k) => {
      const root = triNote(pc(chordAt(s)), st.prevBass);
      let midi = root, len = (k + 1 < hits.length ? hits[k + 1][0] : end) - s, vel = s === 0 ? 0.92 : s % 4 ? 0.74 : 0.84, slide = false;
      if (c === 'o') { midi = root + 12; vel = 0.78; slide = r() < 0.1; }
      else if (c === 'A') {
        const tg = triNote(pc(nx), root), v = r();
        midi = v < 0.45 ? tg - 1 : v < 0.7 ? tg + 1 : tg - 5; vel = 0.76; slide = r() < 0.25;
      } else {
        if (c === '5') midi = root + 7;
        st.prevBass = root;
      }
      return { s, midi, vel, dur: Math.max(0.5, len * plan.bass.factor), slide };
    });
  },
  voices(ac, dry, wet, plan, ctx) {
    const o = { registry: ctx.registry, tag: ctx.tag, t0: ctx.t0 }, sub = () => rng(Math.floor(ctx.r() * 4294967296));
    const ep = createChipChords(ac, dry, wet, plan.chip.chords, plan.ep.lp, { ...o, cap: ctx.cap, r: sub(), log: ctx.log, stepDur: ctx.stepDur });
    const lead = plan.lead ? createChipLead(ac, dry, wet, plan.lead, plan.chip.lead, { ...o, stepDur: ctx.stepDur, r: sub() }) : null;
    return { ep, bass: createTriBass(ac, dry, plan.chip.bass, o), kit: createChipKit(ac, dry, wet, plan.drums.kit, { ...o, stepDur: ctx.stepDur }), lead };
  },
};
